import * as React from 'react';
import propz from 'propz';
import moment from 'moment';
import { formatLondonDate } from 'Src/helpers/service/adminSU/highLevelTournament';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { EligibleSchool } from 'Src/models/school';
import { AGE_GROUPS } from 'Src/consts/school';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { hasUnionParticipant } from 'Src/helpers/tournament/athleteSchools';
import {
  createTournamentNotification,
  getTournamentNotificationAthleteSchools,
  scheduleTournamentNotification,
  sendTournamentNotificationNow
} from 'Src/helpers/service/adminSU/tournamentNotificationTable';
import { TournamentEligibleSchoolsCustomNotificationsView } from '../TournamentEligibleSchoolsCustomNotificationsView/TournamentEligibleSchoolsCustomNotificationsView';
import './TournamentSchoolNotificationComposer.scss';

interface Props {
  user: AppUser;
  history: any;
  location: any;
  audienceContext?: 'eligible' | 'represented' | 'organiserRepresented';
}

interface State {
  tournament?: Tournament;
  schools: EligibleSchool[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string;
  isDirty: boolean;
  isLeaveAlertOpen: boolean;
  pendingPath: string;
}

export class TournamentSchoolNotificationComposer extends React.Component<Props, State> {
  state: State = {
    schools: [],
    isLoading: true,
    isSubmitting: false,
    error: '',
    isDirty: false,
    isLeaveAlertOpen: false,
    pendingPath: ''
  };

  componentDidMount() {
    const tournamentId = this.getTournamentId();
    if (this.props.audienceContext === 'organiserRepresented') {
      if (!tournamentId) {this.setState({isLoading: false, error: 'Tournament was not specified.'}); return;}
      let selection = propz.get(this.props.location, ['state', 'notificationSchools'], []);
      if (!Array.isArray(selection) || !selection.length) {
        try {selection = JSON.parse(window.sessionStorage.getItem(this.getStorageKey(tournamentId)) || '[]');} catch (e) {selection = [];}
      }
      if (!Array.isArray(selection) || !selection.length) {this.setState({isLoading: false, error: 'Select athletes’ schools in Entries before creating a notification.'}); return;}
      const selectedIds = new Set(selection.map(school => String(school.id)));
      Promise.all([getTournament(this.props.user, tournamentId), getTournamentNotificationAthleteSchools(this.props.user, tournamentId)])
        .then(([tournament, rows]) => {
          const schools = rows.filter(school => selectedIds.has(String(school.id)));
          const valid = hasUnionParticipant(tournament) && schools.length === selectedIds.size;
          this.setState({tournament, schools: valid ? schools : [], isLoading: false,
            error: valid ? '' : 'The selected schools changed. Please select athletes’ schools again in Entries.'});
        })
        .catch(() => this.setState({isLoading: false, error: 'We could not load the represented schools. Please reopen this page.'}));
      return;
    }
    const stateSchools = propz.get(this.props.location, ['state', 'notificationSchools'], []);
    let schools = Array.isArray(stateSchools) ? stateSchools : [];
    if (schools.length === 0 && tournamentId) {
      try {
        const stored = window.sessionStorage.getItem(this.getStorageKey(tournamentId));
        schools = stored ? JSON.parse(stored) : [];
      } catch (error) {
        schools = [];
      }
    }

    if (!tournamentId) {
      this.setState({ isLoading: false, error: 'Tournament was not specified.' });
      return;
    }
    if (schools.length === 0) {
      this.setState({ isLoading: false, error: 'No schools were selected for this notification.' });
      return;
    }

    getTournament(this.props.user, tournamentId)
      .then(tournament =>
        this.setState({ tournament, schools, isLoading: false }, () => {
          window.dispatchEvent(
            new CustomEvent('sit:topbar-breadcrumb', {
              detail: `${tournament.name} / ${this.getAudienceLabel()} / Notification`
            })
          );
        })
      )
      .catch(() => this.setState({ isLoading: false, error: 'We could not load this tournament.' }));
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  onDirtyChange = (isDirty: boolean) => {
    if (isDirty === this.state.isDirty) return;
    this.setState({ isDirty });
    if (isDirty) window.addEventListener('beforeunload', this.onBeforeUnload);
    else window.removeEventListener('beforeunload', this.onBeforeUnload);
  };

  onBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };

  getTournamentId = () => {
    const query = parse(this.props.location.search);
    const value = query.tournament || query.tournamentId;
    return Array.isArray(value) ? value[0] : value || '';
  };

  isRepresentedSchoolsContext = () => ['represented', 'organiserRepresented'].includes(this.props.audienceContext);

  getAudienceLabel = () => this.props.audienceContext === 'organiserRepresented' ? 'Athletes’ schools' : (this.isRepresentedSchoolsContext() ? 'Represented schools' : 'Eligible schools');

  getReturnPath = () =>
    this.props.audienceContext === 'organiserRepresented' ? '/tournaments/manage/entries/athleteSchools' : this.isRepresentedSchoolsContext()
      ? '/highLevelTournaments/athleteSchools'
      : '/tournaments/manage/entries/eligible';

  getStorageKey = (tournamentId: string) =>
    this.props.audienceContext === 'organiserRepresented'
      ? `tournamentAthleteSchoolNotificationRecipients:${this.props.user.userId}:${this.props.user.activeSchoolId}:${tournamentId}`
      : `tournamentNotificationRecipients:${this.props.user.userId}:${tournamentId}`;

  goBack = (force = false) => {
    if (this.state.isDirty && !force) {
      this.setState({ isLeaveAlertOpen: true, pendingPath: this.getReturnPath() });
      return;
    }
    this.props.history.push({
      pathname: this.getReturnPath(),
      search: this.props.location.search,
      state: this.props.location.state
    });
  };

  confirmLeave = () => {
    const pathname = this.state.pendingPath || this.getReturnPath();
    this.setState({ isDirty: false, isLeaveAlertOpen: false, pendingPath: '' }, () => {
      this.props.history.push({ pathname, search: this.props.location.search, state: this.props.location.state });
    });
  };

  onSubmit = data => {
    const tournamentId = this.getTournamentId();
    const { submitAction, scheduleAt, ...payload } = data || {};
    let actionDescriptorId = '';
    this.setState({ isSubmitting: true, error: '' });

    createTournamentNotification(this.props.user, tournamentId, {...payload,
      ...(this.props.audienceContext === 'organiserRepresented' ? {recipientAudience: 'ATHLETE_SCHOOLS' as const} : {})})
      .then(response => {
        actionDescriptorId = propz.get(response, ['headers', 'action-descriptor-id'], '');
        if (!actionDescriptorId) throw new Error('Notification action descriptor is missing.');
        return submitAction === 'SCHEDULE'
          ? scheduleTournamentNotification(this.props.user, tournamentId, actionDescriptorId, scheduleAt)
          : sendTournamentNotificationNow(this.props.user, tournamentId, actionDescriptorId);
      })
      .then(() => {
        window.sessionStorage.removeItem(this.getStorageKey(tournamentId));
        this.setState({ isSubmitting: false, isDirty: false }, () => this.goBack(true));
      })
      .catch(() =>
        this.setState({
          isSubmitting: false,
          error: 'The notification could not be sent. Your content is still available; please try again.'
        })
      );
  };

  render() {
    const { tournament, schools, isLoading, isSubmitting, error } = this.state;
    if (isLoading) return <Loader />;

    if (!tournament || (error && schools.length === 0)) {
      return (
        <main className="utNotificationComposer utNotificationComposer--message">
          <h1>{error || 'Tournament not found.'}</h1>
          <button type="button" onClick={() => this.goBack()}>
            Back to {this.getAudienceLabel().toLowerCase()}
          </button>
        </main>
      );
    }

    const isRepresentedSchoolsContext = this.isRepresentedSchoolsContext();
    const participantSchoolsCount = schools.filter(school => Boolean(school.isParticipant)).length;
    const nonParticipantSchoolsCount = schools.length - participantSchoolsCount;
    const representedAthletesCount = schools.reduce(
      (total, school: any) => total + Number(school.athleteCount || 0),
      0
    );
    const ageLabels = Array.from(new Set(tournament.ages || []))
      .sort((first, second) => first - second)
      .map(age => AGE_GROUPS.ENGLISH[age] || String(age));
    const audienceLabel = [
      TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[tournament.gender] || tournament.gender,
      ageLabels.length > 2 ? `${ageLabels[0]}–${ageLabels[ageLabels.length - 1]}` : ageLabels.join(', ')
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <main className="utNotificationComposer">
        <header className="utNotificationComposer__header">
          <button type="button" onClick={() => this.goBack()}>
            <span aria-hidden="true">‹</span> {this.getAudienceLabel()}
          </button>
          <div>
            <h1>{tournament.name}</h1>
            <p>
              {[
                tournament.sport && tournament.sport.name,
                moment(tournament.startTime).isValid()
                  ? formatLondonDate(tournament.startTime)
                  : '',
                tournament.venue &&
                  ((tournament.venue.postcodeData && tournament.venue.postcodeData.postcode) ||
                    tournament.venue.postcode),
                audienceLabel
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <div className="utNotificationComposer__audience">
              {isRepresentedSchoolsContext ? (
                <>
                  <span>
                    <b>{schools.length}</b> represented school{schools.length === 1 ? '' : 's'}
                  </span>
                  <span className="is-participant">
                    <b>{representedAthletesCount}</b> athlete{representedAthletesCount === 1 ? '' : 's'}
                  </span>
                </>
              ) : (
                <>
                  <span>
                    <b>{schools.length}</b> selected
                  </span>
                  <span className="is-participant">
                    <b>{participantSchoolsCount}</b>{' '}
                    {participantSchoolsCount === 1 ? 'participant' : 'participants'}
                  </span>
                  <span className="is-eligible">
                    <b>{nonParticipantSchoolsCount}</b> not participating
                  </span>
                </>
              )}
            </div>
            {this.props.audienceContext === 'organiserRepresented' && <p>This notification goes to staff at the selected athletes’ schools. Each selected school is included once.</p>}
          </div>
        </header>
        <SimpleModal
          isOpen={this.state.isLeaveAlertOpen}
          title="Leave without sending?"
          body="Your notification has unsaved changes. If you leave now, those changes will be lost."
          buttonText="Leave without saving"
          buttonCancelText="Keep editing"
          onButtonClick={this.confirmLeave}
          onCloseClick={() => this.setState({ isLeaveAlertOpen: false, pendingPath: '' })}
          customClass="unionTournamentDeleteModal"
          dialogStyle={{ maxWidth: 500 }}
          isBodyBold={false}
        />
        {error ? <div className="utNotificationComposer__error">{error}</div> : null}
        <section className={isSubmitting ? 'utNotificationComposer__card is-loading' : 'utNotificationComposer__card'}>
          {isSubmitting ? (
            <div className="utNotificationComposer__loading">
              <Loader />
            </div>
          ) : null}
          <TournamentEligibleSchoolsCustomNotificationsView
            user={this.props.user}
            recipientDescription={this.props.audienceContext === 'organiserRepresented' ? 'Selected athletes’ schools in this tournament' : undefined}
            schools={schools}
            tournament={tournament}
            onSubmit={this.onSubmit}
            onCancel={() => this.goBack()}
            onDirtyChange={this.onDirtyChange}
          />
        </section>
      </main>
    );
  }
}
